import { ConfigModule } from 'shared/types'
import { controls } from 'shared/components/controls'

export interface RaceConfig {
	affiliationCode: boolean
	autoScale: boolean
	autoScaleDelay: number
	autoScalePct: number
	autoSort: boolean
	biathlonSortDelay: number
	biathlonVitalScaling: boolean
	classLabels: boolean
	disableTenRowScalingMode: boolean,
	displayClasses: string[]
	easingCurve: string
	filterByParticipantClass: boolean
	gap: 'gap' | 'behind' | 'cumulative' | ''
	gapAfter: number
	gapAfterCal: number
	gapUntil: number
	gapUntilCal: number
	hideNonStarters: boolean
	highlightParticipant: boolean
	laneNumbers: boolean
	boatLaneNumbers: boolean,
	markerDirection: 'ascending' | 'descending',
	maxLanes: number,
	pace: 'pace' | 'avg_pace' | ''
	placeAfter: number
	placeAfterCal: number,
	scale: number
	showPaceboat: 'not shown' | 'shown' | 'marker' | 'marker with line' | 'circle' | 'circle with line'
	paceBoatPace: boolean
	sortAfter: number
	sortAfterCal: number
	sortRaces: boolean
	startAt: number
	vitalsExpansionRatio: number
	vitalsNameFontSize: string
	zoomInTrigger: number
	zoomOutTrigger: number
}


import './blob'
declare module './blob' {
	interface ConfigBlob {
		raceConfig: RaceConfig
	}
}

export const raceConfig: ConfigModule<RaceConfig> = {
	page: 'race',
	panel: 'Options',
	isTheme: false,
	fields: {
		scale: {
			default: 0.5,
			control: {
				component: 'vertical-slider',
				label: 'Field of View',
				options: {
					min: 0.01,
					step: 0.01,
					max: 1,
					topLabel: '1%',
					bottomLabel: '100%'
				}
			}
		},
		autoSort: {
			default: true
		},
		sortAfter: {
			default: 100
		},
		sortAfterCal: {
			default: 10
		},
		classLabels: {
			default: false,
			control: controls.Checkbox('Class Labels'),
			group: 'Options',
			hideDuringRace: true
		},
		laneNumbers: {
			default: true,
			control: controls.Checkbox('Lane Numbers'),
			group: 'Options'
		},
		markerDirection: {
			default: 'descending',
			control: controls.Radio('Marker Direction', {
				Ascending: 'ascending',
				Descending: 'descending'
			}),
			private: false,
			hideDuringRace: false
		},
		maxLanes: {
			default: 18,
			control: controls.Number('Max Lanes', 18, 1, 25),
		},
		boatLaneNumbers: {
			default: true,
			control: controls.Checkbox('Boat Lane Numbers'),
			group: 'Options'
		},
		affiliationCode: {
			default: true,
			control: controls.Checkbox('Affiliation Code'),
			group: 'Options'
		},
		sortRaces: {
			default: true,
			control: controls.Checkbox('Sort by Place'),
			group: 'Options'
		},
		disableTenRowScalingMode: {
			default: false,
			control: controls.Checkbox('Disable 10 Row Scaling'),
			group: 'Options',
			private: true,
			hideDuringRace: true
		},
		hideNonStarters: {
			default: false,
			control: controls.Checkbox('Hide Non-Starters'),
			group: 'Options'
		},
		highlightParticipant: {
			default: true,
			control: controls.Checkbox('Highlight Participant'),
			group: 'Options',
			private: true
		},
		biathlonVitalScaling: {
			default: false,
			control: controls.Checkbox('Biathlon Scaling'),
			group: 'Options',
			private: true,
			hideDuringRace: true
		},
		biathlonSortDelay: {
			default: 10,
			control: controls.Number('Biathlon Sort Delay (s)'),
			group: 'Options',
			private: true,
			hideDuringRace: true
		},
		startAt: {
			default: 1,
			control: controls.Number('Start At', 1),
			hideDuringRace: true
		},
		pace: {
			default: 'pace',
			control: controls.Radio('Pace Display', {
				'Instantaneous Pace': 'pace',
				'Average Pace': 'avg_pace',
				'Average Watts': 'avg_watts',
				Watts: 'watts'
			}),
			hideDuringRace: true
		},
		gap: {
			default: 'gap',
			control: controls.Radio('Gap Display', {
				Gap: 'gap',
				'Units Behind': 'behind',
				Cumulative: 'cumulative'
			}),
			private: true,
			hideDuringRace: true
		},
		showPaceboat: {
			default: 'marker',
			control: controls.Radio('Show Paceboat', {
				'Not Shown': 'not shown',
				'Shown in Lane': 'shown',
				'As Marker': 'marker',
				'As Marker with Line': 'marker with line',
				'As Circle': 'circle',
				'As Circle with Line': 'circle with line'
			}),
			hideDuringRace: true,
			private: true
		},
		paceBoatPace: {
			default: false,
			control: controls.Checkbox('Show Marker with Pace'),
			hideDuringRace: true
		},
		gapAfter: {
			default: 100
		},
		gapUntil: {
			default: 0
		},
		gapAfterCal: {
			default: 0
		},
		gapUntilCal: {
			default: 0
		},
		displayClasses: {
			default: [],
			private: true,
			control: { component: 'display-classes' },
			hideDuringRace: true,
			showToSpectator: true
		},
		filterByParticipantClass: {
			default: true,
			showToParticipant: true,
			participantOnly: true,
			control: controls.Checkbox('Only My Class'),
		},
		easingCurve: {
			default: 'Square'
		},
		placeAfter: {
			default: 100
		},
		placeAfterCal: {
			default: 10
		},
		autoScale: {
			default: true,
			control: controls.Checkbox('Auto Scale'),
			group: 'Auto Scale'
		},
		autoScalePct: {
			default: 80,
			control: controls.Number('Track Top %'),
			group: 'Auto Scale'
		},
		autoScaleDelay: {
			default: 10,
			control: controls.Number('Auto Delay (sec)'),
			group: 'Auto Scale'
		},
		vitalsNameFontSize: {
			default: '0.6em'
		},
		vitalsExpansionRatio: {
			default: 1,
			private: true,
			control: {
				component: 'vertical-slider',
				label: 'Vitals Width',
				options: {
					min: 0.5,
					step: 0.01,
					max: 1.5,
					topLabel: 'Narrow',
					bottomLabel: 'Wide'
				}
			}
		},
		zoomInTrigger: {
			default: 1
		},
		zoomOutTrigger: {
			default: 1
		}
	}
}
